import React, { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { pureUiTheme } from 'pureUi/pureUiTheme';
import { dragAndDrop } from '@formkit/drag-and-drop';
import { isEqual } from 'lodash';

export interface SortableListProps<T> {
  className?: string;
  items?: T[] | null;
  keySelector: (item: T, index: number) => number | string;
  renderItem: (item: T, index: number) => React.ReactNode;
  onChange: (items: T[]) => void;
  children?: React.ReactNode;
}

const Wrapper = styled.div``;

const Content = styled.div`
  width: 100%;
`;

interface IInternalItem<T> {
  key: string | number;
  originalItem: T;
  originalIndex: number;
}

export const SortableList = <T,>(props: SortableListProps<T>) => {
  const { className, items, keySelector, renderItem, onChange } = props;
  const parentRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<IInternalItem<T>[]>([]);

  // Build internal items array with keys and original items
  const internalItems: IInternalItem<T>[] = items
    ? items.map((item, index) => ({
        key: keySelector(item, index),
        originalItem: item,
        originalIndex: index,
      }))
    : [];

  // Keep ref in sync with latest internal items
  useEffect(() => {
    itemsRef.current = internalItems;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const handleSetValues = useCallback(
    (newInternalItems: IInternalItem<T>[]) => {
      const newItems = newInternalItems.map(item => item.originalItem);
      const currentItems = itemsRef.current.map(item => item.originalItem);

      if (!isEqual(newItems, currentItems)) {
        onChange(newItems);
      }
    },
    [onChange]
  );

  // Initialize drag and drop when items change
  const itemsLength = items?.length ?? 0;
  useEffect(() => {
    if (!parentRef.current || itemsLength === 0) {
      return;
    }

    dragAndDrop({
      parent: parentRef.current,
      getValues: () => itemsRef.current,
      setValues: handleSetValues,
      config: {
        draggingClass: 'dragging',
      },
    });
  }, [itemsLength, handleSetValues]);

  if (!items) {
    return null;
  }

  return (
    <Wrapper className={className}>
      <Content ref={parentRef}>
        {internalItems.map(item => (
          <div key={item.key} data-key={item.key}>
            {renderItem(item.originalItem, item.originalIndex)}
          </div>
        ))}
      </Content>
    </Wrapper>
  );
};

const StyledSortableList = styled(SortableList)`
  border: ${pureUiTheme.colorRoles.lightGreyBorder} 1px solid;
  background-color: ${pureUiTheme.colors.grayDepth3};
  cursor: grab;
`;

const StyledSortableItem = styled.div`
  border-bottom: ${pureUiTheme.colorRoles.lightGreyBorder} 1px solid;
  background-color: #fff;

  &:last-of-type {
    border-bottom: 0;
  }

  &.dragging {
    opacity: 0.9;
    border: ${pureUiTheme.colorRoles.lightGreyBorder} 1px solid;
    box-shadow: 0 5px 5px -5px rgba(0, 0, 0, 0.2), 0 -5px 5px -5px rgba(0, 0, 0, 0.2);
  }
`;

export const DefaultSortableList = <T,>(props: SortableListProps<T>) => {
  const { renderItem, ...rest } = props;

  const styledRenderItem = useCallback(
    (item: T, index: number) => <StyledSortableItem>{renderItem(item, index)}</StyledSortableItem>,
    [renderItem]
  );

  return <StyledSortableList renderItem={styledRenderItem} {...rest} />;
};

export default DefaultSortableList;
